// valet-keychain-qr / qr-generator-org-picker — qr-generator entry point.
//
// The backend qr-batches surface is scoped `super-admin/organizations/:orgId
// /locations/:locationId/qr-batches` (super_admin-only, cross-org). This page
// lets the operator pick the organization (searchable combobox, filtered
// client-side by name) and then the location (a dependent <select> loaded
// from GET /super-admin/organizations/:orgId/locations once an org is
// chosen), instead of pasting raw UUIDs.

import { useTranslation } from '@jq4race/i18n';
import { Button, Card, CardContent, Label } from '@jq4race/ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { listOrganizationLocations, listOrganizations } from '../../api/organizations';
import { LocationSelect } from '../components/LocationSelect';
import { OrgCombobox } from '../components/OrgCombobox';

export function QrGeneratorSelectPage() {
  const { t } = useTranslation('qr-generator');
  const navigate = useNavigate();
  const [orgId, setOrgId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const orgsQuery = useQuery({
    queryKey: ['qr-generator', 'organizations'],
    queryFn: listOrganizations,
  });

  const locationsQuery = useQuery({
    queryKey: ['qr-generator', 'organization-locations', orgId],
    queryFn: () => listOrganizationLocations(orgId),
    enabled: orgId !== '',
  });

  // Dependent select: a previously chosen location almost certainly doesn't
  // belong to the newly chosen org (and even a same-id coincidence would be
  // wrong to carry over silently), so clear it every time the org changes.
  // orgId isn't read in the effect body — it's the intentional trigger.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  useEffect(() => {
    setLocationId('');
  }, [orgId]);

  const orgOptions = (orgsQuery.data?.organizations ?? []).map((org) => ({
    id: org.id,
    name: org.name,
  }));
  const locationOptions = (locationsQuery.data?.locations ?? []).map((location) => ({
    id: location.id,
    name: location.name,
  }));

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!orgId || !locationId) {
      setError(t('select.bothRequired'));
      return;
    }
    setError(null);
    void navigate({
      to: '/super-admin/qr-generator/$orgId/$locationId',
      params: { orgId, locationId },
    });
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.title')}
        description={t('page.description')}
      />

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          <h2 className="mb-2 text-[15px] font-medium text-jq-charcoal">{t('select.title')}</h2>
          <p className="mb-6 text-[13px] text-jq-graphite">{t('select.description')}</p>

          <form onSubmit={handleSubmit} className="grid gap-6 sm:grid-cols-2">
            <div>
              <Label htmlFor="org" className="mb-1.5 block text-[13px] font-medium">
                {t('select.orgLabel')}
              </Label>
              <OrgCombobox
                id="org"
                value={orgId}
                onChange={setOrgId}
                options={orgOptions}
                loading={orgsQuery.isLoading}
                placeholder={t('select.orgPlaceholder')}
                loadingLabel={t('select.orgLoading')}
                noResultsLabel={t('select.orgNoResults')}
                aria-describedby={orgsQuery.isError ? 'org-load-error' : undefined}
              />
              {orgsQuery.isError && (
                <p id="org-load-error" role="alert" className="mt-1 text-[12px] text-jq-danger">
                  {t('select.orgLoadError')}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="location" className="mb-1.5 block text-[13px] font-medium">
                {t('select.locationLabel')}
              </Label>
              <LocationSelect
                id="location"
                value={locationId}
                onChange={setLocationId}
                options={locationOptions}
                orgSelected={orgId !== ''}
                loading={locationsQuery.isLoading}
                placeholder={
                  orgId === ''
                    ? t('select.locationPlaceholderNoOrg')
                    : t('select.locationPlaceholder')
                }
                loadingLabel={t('select.locationLoading')}
                emptyLabel={t('select.locationEmpty')}
                aria-describedby={locationsQuery.isError ? 'location-load-error' : undefined}
              />
              {locationsQuery.isError && (
                <p
                  id="location-load-error"
                  role="alert"
                  className="mt-1 text-[12px] text-jq-danger"
                >
                  {t('select.locationLoadError')}
                </p>
              )}
            </div>

            {error && (
              <p role="alert" className="sm:col-span-2 text-[13px] text-jq-danger">
                {error}
              </p>
            )}

            <div className="sm:col-span-2">
              <Button type="submit" size="sm">
                {t('select.submit')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
